import { useEffect, useState } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import CapabilitiesChecklist from "@/components/admin/CapabilitiesChecklist";

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
  toast.success("Copied!");
}

export default function CreateEmployeeModal({
  open,
  empForm,
  setEmpForm,
  createdEmp,
  setCreatedEmp,
  onClose,
  onCreated,
}) {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  // Hydrate the capabilities default the first time the role flips to
  // "admin" inside this modal — all-checked, per product requirement.
  const [capabilities, setCapabilities] = useState([]);
  useEffect(() => {
    if (empForm.role === "admin" && capabilities.length === 0) {
      // Fetch catalog defaults so we honor whatever backend says.
      API.get("/admin/capabilities/catalog")
        .then(({ data }) => setCapabilities(data.defaults_on_create || []))
        .catch(() => setCapabilities([]));
    }
    if (empForm.role !== "admin" && capabilities.length > 0) {
      setCapabilities([]);
    }
  }, [empForm.role]);

  const create = async () => {
    if (!empForm.name.trim()) { toast.error("Name is required"); return; }
    if (empForm.role === "admin" && !isSuperAdmin) {
      toast.error("Only a super-admin can create admin accounts");
      return;
    }
    try {
      const payload = { ...empForm };
      if (empForm.role === "admin") payload.capabilities = capabilities;
      const { data } = await API.post("/admin/create-employee", payload);
      setCreatedEmp(data);
      toast.success("Employee account created!");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create employee");
    }
  };

  const showNpField = empForm.role === "prescriber" && empForm.prescriber_type === "np";
  const showPrescriberFields = empForm.role === "prescriber";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
        dirtyGuardMessage="Discard this new employee draft and close?"
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Create Staff Account</DialogTitle>
          <DialogDescription>
            Provision a new employee account. A one-time password is generated automatically — make sure to copy it before closing this dialog.
          </DialogDescription>
        </DialogHeader>
        {createdEmp && (
          <div className="space-y-4" data-testid="employee-created">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
              <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Account Created!</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-lg p-3 border">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">User ID</p>
                    <p className="font-mono font-bold text-purple-600 text-lg">{createdEmp.employee_id}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(createdEmp.employee_id)}><Copy className="w-4 h-4" /></Button>
                </div>
                <div className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-lg p-3 border">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Password</p>
                    <p className="font-mono font-bold text-slate-900 dark:text-white">{createdEmp.password}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(createdEmp.password)}><Copy className="w-4 h-4" /></Button>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Name</p>
                  <p className="font-medium">{createdEmp.name}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Role</p>
                  <p className="font-medium capitalize">{createdEmp.role.replace("_", " ")}{createdEmp.role === "prescriber" && createdEmp.prescriber_type ? ` (${createdEmp.prescriber_type})` : ""}</p>
                </div>
              </div>
            </div>
            <p className="text-xs text-amber-600 text-center">Save these credentials. The password cannot be recovered later.</p>
            <Button className="w-full rounded-lg" onClick={onClose}>Done</Button>
          </div>
        )}
        {!createdEmp && (
          <div className="space-y-4" data-testid="create-employee-form">
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Full Name</Label>
              <Input value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} placeholder="Dr. John Smith" className="mt-1 rounded-xl" data-testid="emp-name-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Role</Label>
              <Select value={empForm.role} onValueChange={(v) => setEmpForm({ ...empForm, role: v })}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="emp-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prescriber">Prescriber</SelectItem>
                  <SelectItem value="admin" disabled={!isSuperAdmin}>
                    Admin{!isSuperAdmin ? " (super-admin only)" : ""}
                  </SelectItem>
                  <SelectItem value="customer_service">Team Member</SelectItem>
                  <SelectItem value="it">Tech Support (Audit &amp; Error Triage)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {empForm.role === "admin" && isSuperAdmin && (
              <CapabilitiesChecklist value={capabilities} onChange={setCapabilities} />
            )}
            {showPrescriberFields && (
              <>
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Prescriber Type</Label>
                  <Select value={empForm.prescriber_type} onValueChange={(v) => setEmpForm({ ...empForm, prescriber_type: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="emp-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="physician">Physician (Full prescribing authority)</SelectItem>
                      <SelectItem value="np">Nurse Practitioner (Under supervision)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {showNpField && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Supervising Physician Name</Label>
                    <Input value={empForm.supervising_physician_name} onChange={(e) => setEmpForm({ ...empForm, supervising_physician_name: e.target.value })} placeholder="Dr. Jane Doe" className="mt-1 rounded-xl" data-testid="emp-supervisor-input" />
                    <p className="text-xs text-amber-600 mt-1">ERX will include: &quot;Prescribed under supervision of [name]&quot;</p>
                  </div>
                )}
              </>
            )}
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Personal Email (for password resets)</Label>
              <Input value={empForm.personal_email} onChange={(e) => setEmpForm({ ...empForm, personal_email: e.target.value })} placeholder="john@personal.com" className="mt-1 rounded-xl" data-testid="emp-email-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Password (leave blank to auto-generate)</Label>
              <Input value={empForm.password} onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} placeholder="Auto-generate" className="mt-1 rounded-xl" data-testid="emp-password-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={create} data-testid="confirm-create-employee-btn">Create Account</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
